/**
 * Modal Component
 *
 * Standardized modal dialog for Admin UI.
 * Handles overlay, keyboard interactions, and layout.
 * Supports premium mode with enhanced animations.
 *
 * @package PresszoneComments
 */

import { el, clear, __ } from '../utils/dom.js';

export default class Modal {
    constructor(options = {}) {
        this.options = {
            title: '',
            width: '600px',
            id: 'presszone-comments-modal',
            containerId: 'presszone-comments-modal-container',
            onClose: null,
            premium: false, // Enable premium styling
            icon: null, // Optional title icon
            headerAction: null, // Optional action button for header
            ...options
        };


        this.container = document.getElementById(this.options.containerId);
        if (!this.container) {
            // Create if missing (fallback) - this happens when Modal is instantiated before App()
            this.container = el('div', { id: this.options.containerId });
            document.body.appendChild(this.container);
        }

        // Inherit theme class from the comments wrapper so modal respects dark/light mode
        const wrapper = document.querySelector('.presszone-comments-wrapper');
        if (wrapper) {
            // First, clear all theme and styling classes from the container
            this.container.classList.remove(
                'dark-mode',
                'presszone-comments-theme--dark',
                'presszone-comments-theme--light',
                'presszone-comments-theme--inherit',
                'presszone-comments-styling--square',
                'presszone-comments-styling--rounded',
                'presszone-comments-styling--pill',
                'presszone-comments-styling--borderless',
                'presszone-comments-border--standard',
                'presszone-comments-border--thick',
                'presszone-comments-border--extra-thick'
            );

            // Then add the appropriate theme class based on wrapper state
            if (wrapper.classList.contains('dark-mode') || wrapper.classList.contains('presszone-comments-theme--dark')) {
                this.container.classList.add('dark-mode', 'presszone-comments-theme--dark');
            } else if (wrapper.classList.contains('presszone-comments-theme--light')) {
                this.container.classList.add('presszone-comments-theme--light');
            } else if (wrapper.classList.contains('presszone-comments-theme--inherit')) {
                this.container.classList.add('presszone-comments-theme--inherit');
            }

            // Also inherit styling class (square/rounded/pill) for border-radius settings
            if (wrapper.classList.contains('presszone-comments-styling--square')) {
                this.container.classList.add('presszone-comments-styling--square');
            } else if (wrapper.classList.contains('presszone-comments-styling--pill')) {
                this.container.classList.add('presszone-comments-styling--pill');
            } else if (wrapper.classList.contains('presszone-comments-styling--rounded')) {
                this.container.classList.add('presszone-comments-styling--rounded');
            }

            // Inherit border thickness settings
            if (wrapper.classList.contains('presszone-comments-border--standard')) {
                this.container.classList.add('presszone-comments-border--standard');
            } else if (wrapper.classList.contains('presszone-comments-border--thick')) {
                this.container.classList.add('presszone-comments-border--thick');
            } else if (wrapper.classList.contains('presszone-comments-border--extra-thick')) {
                this.container.classList.add('presszone-comments-border--extra-thick');
            }

            // Inherit borderless setting
            if (wrapper.classList.contains('presszone-comments-styling--borderless')) {
                this.container.classList.add('presszone-comments-styling--borderless');
            }

            // Inherit padding data attribute
            const paddingSetting = wrapper.dataset.padding;
            if (paddingSetting) {
                this.container.dataset.padding = paddingSetting;
            }
        }

        this.element = null;
        this.modalEl = null;
        this.isOpen = false;
        this.isClosing = false;
        this.triggerElement = null; // Store focus return element

        // Bind methods
        this.handleEsc = this.handleEsc.bind(this);
        this.handleTab = this.handleTab.bind(this);
    }

    /**
     * Get CSS class names based on premium mode.
     */
    getClasses() {
        const p = this.options.premium;
        return {
            overlay: p ? 'presszone-comments-modal-overlay presszone-comments-modal-overlay--premium' : 'presszone-comments-modal-overlay',
            modal: p ? 'presszone-comments-modal presszone-comments-modal--premium' : 'presszone-comments-modal',
            header: p ? 'presszone-comments-modal__header presszone-comments-modal__header--premium' : 'presszone-comments-modal__header',
            title: p ? 'presszone-comments-modal__title presszone-comments-modal__title--premium' : 'presszone-comments-modal__title',
            close: p ? 'presszone-comments-modal__close presszone-comments-modal__close--premium' : 'presszone-comments-modal__close',
            content: p ? 'presszone-comments-modal__content presszone-comments-modal__content--premium' : 'presszone-comments-modal__content',
            tabs: p ? 'presszone-comments-modal-tabs' : 'presszone-comments-tabs',
            actions: p ? 'presszone-comments-modal__actions' : 'presszone-comments-modal__actions'
        };
    }

    /**
     * Render the modal structure.
     * @param {HTMLElement|string} content - The body content
     * @param {HTMLElement|Array} actions - Footer action buttons
     * @param {Array} tabs - Optional tabs configuration
     */
    render(content, actions = null, tabs = null) {
        const classes = this.getClasses();

        // Build title element
        const titleContent = [];
        if (this.options.icon && this.options.premium) {
            titleContent.push(
                el('span', { class: 'presszone-comments-modal__title-icon' }, this.options.icon)
            );
        }
        titleContent.push(this.options.title || '');

        this.element = el('div', {
            class: classes.overlay,
            onclick: (e) => {
                if (e.target.classList.contains('presszone-comments-modal-overlay') ||
                    e.target.classList.contains('presszone-comments-modal-overlay--premium')) {
                    this.close();
                }
            },
            role: 'dialog',
            'aria-modal': 'true',
            'aria-labelledby': 'presszone-comments-modal-title'
        },
            this.modalEl = el('div', {
                class: classes.modal + (this.options.extraClass ? ` ${this.options.extraClass}` : '') + ' presszone-comments-animated-item--pop',
                style: { maxWidth: this.options.width }
            },
                // Header
                el('header', { class: classes.header },
                    el('h2', {
                        id: 'presszone-comments-modal-title',
                        class: classes.title
                    }, ...titleContent),
                    el('div', { class: 'presszone-comments-modal__header-controls' },
                        // Header action button (optional)
                        this.options.headerAction ? el('div', { class: 'presszone-comments-modal__header-action' }, this.options.headerAction) : null,
                        el('button', {
                            class: classes.close,
                            onclick: () => this.close(),
                            title: __('Close', 'comments-press-zone'),
                            'aria-label': __('Close', 'comments-press-zone')
                        }, '×')
                    )
                ),

                // Tabs (optional) - in premium mode, tabs are outside header
                tabs ? el('div', { class: classes.tabs, role: 'tablist' }, ...tabs) : null,

                // Content
                el('div', { class: classes.content, role: 'tabpanel' }, content),

                // Footer (Actions)
                actions ? el('div', { class: classes.actions },
                    ...(Array.isArray(actions) ? actions : [actions])
                ) : null
            )
        );

        this.container.appendChild(this.element);
    }

    /**
     * Update only the content and tabs without re-rendering the entire modal.
     * This prevents the modal pop-in animation from re-triggering on tab changes.
     * @param {HTMLElement|string} content - The new body content
     * @param {Array} tabs - Optional new tabs configuration
     */
    updateContent(content, tabs = null) {
        if (!this.modalEl) {
            // Modal not rendered yet, fall back to full render
            this.render(content, null, tabs);
            return;
        }

        const classes = this.getClasses();

        // Update tabs if provided
        const tabsContainer = this.modalEl.querySelector(`.${classes.tabs.split(' ')[0]}`);
        if (tabs) {
            if (tabsContainer) {
                clear(tabsContainer);
                tabs.forEach(tab => tabsContainer.appendChild(tab));
            } else {
                // Insert tabs after header if they don't exist
                const header = this.modalEl.querySelector('header');
                if (header) {
                    const newTabsEl = el('div', { class: classes.tabs, role: 'tablist' }, ...tabs);
                    header.after(newTabsEl);
                }
            }
        }

        // Update content
        const contentContainer = this.modalEl.querySelector(`.presszone-comments-modal__content, .presszone-comments-modal__content--premium`);
        if (contentContainer) {
            clear(contentContainer);
            if (typeof content === 'string') {
                contentContainer.innerHTML = content;
            } else if (content) {
                contentContainer.appendChild(content);
            }
        }
    }

    /**
     * Open the modal.
     */
    open() {
        if (!this.element) {
            console.warn('Modal content not rendered. Call render() first.');
            return;
        }

        // Store current focus for return
        this.triggerElement = document.activeElement;

        this.container.style.display = 'block';
        this.isOpen = true;
        this.isClosing = false;
        document.body.style.overflow = 'hidden'; // Prevent background scroll
        document.addEventListener('keydown', this.handleEsc);
        document.addEventListener('keydown', this.handleTab);

        // Focus first focusable element
        requestAnimationFrame(() => {
            const firstInput = this.element.querySelector('input, select, textarea, button:not(.presszone-comments-modal__close):not(.presszone-comments-modal__close--premium)');
            if (firstInput) {
                firstInput.focus();
            }
        });
    }

    /**
     * Close the modal with optional animation.
     */
    close() {
        if (this.isClosing) return;

        // For premium mode, add closing animation
        if (this.options.premium && this.modalEl) {
            this.isClosing = true;
            this.modalEl.classList.add('is-closing');
            this.element.style.opacity = '0';
            this.element.style.transition = 'opacity 150ms cubic-bezier(0.175, 0.885, 0.32, 1.275)';

            setTimeout(() => {
                this.finalizeClose();
            }, 150);
        } else {
            this.finalizeClose();
        }
    }

    /**
     * Complete the close operation.
     */
    finalizeClose() {
        if (this.element && this.element.parentNode) {
            this.element.remove();
        }

        // Only hide container if no other modals are present
        if (this.container.children.length === 0) {
            this.container.style.display = 'none';
            document.body.style.overflow = '';
        }

        this.isOpen = false;
        this.isClosing = false;
        this.element = null;
        this.modalEl = null;

        document.removeEventListener('keydown', this.handleEsc);
        document.removeEventListener('keydown', this.handleTab);

        // Return focus to trigger element
        if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
            this.triggerElement.focus();
        }

        if (typeof this.options.onClose === 'function') {
            this.options.onClose();
        }
    }

    /**
     * Handle Escape key to close.
     */
    handleEsc(e) {
        if (e.key === 'Escape' && this.isOpen) {
            this.close();
        }
    }

    /**
     * Handle Tab key for focus trap.
     */
    handleTab(e) {
        if (e.key !== 'Tab' || !this.isOpen || !this.element) return;

        const focusableElements = this.element.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstEl = focusableElements[0];
        const lastEl = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
            // Shift + Tab: going backwards
            if (document.activeElement === firstEl) {
                e.preventDefault();
                lastEl.focus();
            }
        } else {
            // Tab: going forward
            if (document.activeElement === lastEl) {
                e.preventDefault();
                firstEl.focus();
            }
        }
    }

    /**
     * Update the title dynamically.
     */
    setTitle(title) {
        this.options.title = title;
        if (this.element) {
            const h2 = this.element.querySelector('h2');
            if (h2) h2.textContent = title;
        }
    }
}
